import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AnimatedIcon from '../IssuesSection/AnimatedIcon/romb';
import IssueSectionSkeleton from './IssueSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import { IMAGES_BASE_URL } from '../../config/constants';
import './Issue.scss';

const IssueSection = ({ page, skeletonItemsCount = 6, subCountSkeleton = 4 }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const { data: issueJson, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/issue.json');

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const data = issueJson?.issueData?.[page];
    
    if (data && 
        data.accentText && 
        data.title && 
        data.subtitle && 
        data.buttonText && 
        data.buttonLink && 
        data.links && 
        Array.isArray(data.links) && 
        data.links.length > 0) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, issueJson, page]);

  const issueData = useMemo(() => {
    return issueJson?.issueData?.[page] || null;
  }, [issueJson, page]);

  const getTranslatedText = useCallback((textData) => {
    if (typeof textData === 'object' && textData[currentLang] !== undefined) {
      return textData[currentLang];
    }
    if (typeof textData === 'object' && textData.en !== undefined) {
      return textData.en;
    }
    return textData;
  }, [currentLang]);

  const translatedData = useMemo(() => {
    if (!issueData) return null;

    const globalUseAnimatedIcon = issueData.useAnimatedIcon;

    return {
      accentText: getTranslatedText(issueData.accentText),
      title: getTranslatedText(issueData.title),
      subtitle: getTranslatedText(issueData.subtitle),
      buttonText: getTranslatedText(issueData.buttonText),
      buttonLink: issueData.buttonLink,
      links: issueData.links.map(link => ({
        ...link,
        title: getTranslatedText(link.title),
        description: getTranslatedText(link.description),
        useAnimatedIcon: link.useAnimatedIcon !== undefined ? link.useAnimatedIcon : globalUseAnimatedIcon,
        altText: Array.isArray(getTranslatedText(link.title)) 
          ? getTranslatedText(link.title).join(' ') 
          : getTranslatedText(link.title)
      }))
    };
  }, [issueData, getTranslatedText]);

  const hasValidData = useMemo(() => 
    translatedData && translatedData.links && translatedData.links.length > 0,
    [translatedData]
  );

  if (isLoading) {
    return <IssueSectionSkeleton itemsCount={skeletonItemsCount} subCount={subCountSkeleton}/>;
  }

  if (hasError || !issueData || !translatedData || !hasValidData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const { accentText, title, subtitle, buttonText, buttonLink, links } = translatedData;

  return (
    <div className="issue__services__section" id="need__section">
      <div className="left__side__issue__services__section">
        <div className="services__issue__content">
          <AnimatedOnScroll animation="fade-up" delay={0}>
          <p className="services__issue__accent">{accentText}</p>
          </AnimatedOnScroll>
          <AnimatedOnScroll animation="fade-up" delay={100}>
          <h4 className="services__issue__title">{title}</h4>
          </AnimatedOnScroll>
          <AnimatedOnScroll animation="fade-up" delay={200}>
          <h5 className="services__issue__subtitle">{subtitle}</h5>
          </AnimatedOnScroll>
          <AnimatedOnScroll animation="fade-up" delay={300}>
          <Link to={buttonLink} className="services__issue__button">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            {buttonText}
          </Link>
          </AnimatedOnScroll>
        </div>
      </div>
      <div className="right__side__issue__services__section">
        {links.map((link) => (
          <div className="services__issue__links__item" key={link.id}>
            <AnimatedOnScroll animation="fade-up" delay={0}>
            <div className="issue__icon">
              {link.useAnimatedIcon ? (
                <AnimatedIcon />
              ) : (
                <img
                  src={`${IMAGES_BASE_URL}${link.icon}`}
                  alt={link.altText}
                  className="issue__icon"
                  loading="lazy"
                />
              )}
            </div>
            <h3 className="issue__link__item">
              {Array.isArray(link.title) ? (
                link.title.map((line, index) => (
                  <span key={index}>
                    {line}
                    <br />
                  </span>
                ))
              ) : (
                <span>{link.title}</span>
              )}
            </h3>
            <p className="services__issue__link__text">
              {link.description}
            </p>
            </AnimatedOnScroll>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(IssueSection);